import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { TIERS, EMAIL, PAYPAL_DONATION_URL } from "../lib/content";

const rise = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.7 } };

function DonateBox() {
  const [cfg, setCfg] = useState(null);
  const [amount, setAmount] = useState("50");
  const [thanksEmail, setThanksEmail] = useState("");
  const [thanksBusy, setThanksBusy] = useState(false);
  const [thanksSent, setThanksSent] = useState(false);
  useEffect(() => { api.get("/paypal/config").then((r) => setCfg(r.data)).catch(() => setCfg({ configured: false })); }, []);
  const liveSdk = cfg?.configured && cfg?.env === "live";
  const linkMode = !liveSdk && Boolean(PAYPAL_DONATION_URL);
  const sdkMode = Boolean(cfg?.configured) && !linkMode;

  const submitThanks = async (e) => {
    e.preventDefault();
    setThanksBusy(true);
    try {
      await api.post("/thanks", { email: thanksEmail });
      setThanksSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Couldn't send just now — please try again."));
    } finally {
      setThanksBusy(false);
    }
  };

  return (
    <div className="bg-[#1A1A1A] text-[#F5F2EC] rounded-2xl p-7" data-testid="donate-box">
      <h3 className="font-display font-extrabold uppercase text-xl mb-2">Give once</h3>
      <p className="text-xs opacity-70 mb-5">Every dollar goes to prevention, education, hui and whānau support in Ōtautahi.</p>
      {linkMode ? (
        <div data-testid="donate-link-box">
          <div className="flex flex-wrap gap-2 mb-5">
            {["$10 – $5,000", "PayPal or card", "No account needed"].map((t) => (
              <span key={t} className="rounded-full px-4 py-1.5 text-xs font-bold bg-white/10">{t}</span>
            ))}
          </div>
          <a href={PAYPAL_DONATION_URL} target="_blank" rel="noreferrer" data-testid="donate-paypal-link-button"
            className="flex items-center justify-center gap-2 w-full bg-[#FFC439] hover:bg-[#F2B619] text-black font-bold rounded-full py-3.5 text-sm transition-colors">
            Give securely with PayPal <ExternalLink size={15} />
          </a>
          <p className="text-[10px] opacity-50 mt-3 text-center">Real donations, processed on the movement's own secure PayPal page.</p>
          <div className="mt-5 pt-4 border-t border-white/10">
            {thanksSent ? (
              <p className="text-xs text-[#F59E0B]" data-testid="donor-thanks-sent">Ngā mihi — your thank-you is on its way to your inbox.</p>
            ) : (
              <>
                <p className="text-[11px] opacity-70 mb-2">Left a gift? Pop your email in and we'll send a proper thank-you.</p>
                <form className="flex gap-2" onSubmit={submitThanks}>
                  <input type="email" required value={thanksEmail} onChange={(e) => setThanksEmail(e.target.value)}
                    placeholder="you@example.com" data-testid="donor-thanks-email" maxLength={120}
                    className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-xs focus:outline-none focus:border-[#F59E0B]" />
                  <button type="submit" disabled={thanksBusy} data-testid="donor-thanks-submit"
                    className="bg-[#1E4B3E] hover:bg-[#16382e] disabled:opacity-50 text-white text-xs font-bold rounded-full px-4 py-2 transition-colors">
                    {thanksBusy ? "Sending…" : "Send"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {["20", "50", "100", "250"].map((a) => (
              <button key={a} data-testid={`donate-amount-${a}`} onClick={() => setAmount(a)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${amount === a ? "bg-[#F59E0B] text-black" : "bg-white/10 hover:bg-white/20"}`}>
                ${a}
              </button>
            ))}
            <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="donate-custom-amount"
              className="w-24 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs focus:outline-none focus:border-[#F59E0B]" placeholder="Custom" />
          </div>
          {cfg === null ? null : sdkMode ? (
            <PayPalScriptProvider options={{ "client-id": cfg.client_id, currency: "NZD" }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "pill", height: 45 }}
                createOrder={async () => {
                  const r = await api.post("/paypal/orders", { amount: parseFloat(amount) || 10, kind: "donation", label: "Break the Cycle Movement donation" });
                  return r.data.id;
                }}
                onApprove={async (data) => {
                  try {
                    await api.post(`/paypal/orders/${data.orderID}/capture`);
                    toast.success("Ngā mihi nui — your donation was received.");
                  } catch {
                    toast.error("Payment capture failed — please contact us and we'll sort it out.");
                  }
                }}
              />
            </PayPalScriptProvider>
          ) : (
            <p className="text-xs opacity-80 bg-white/10 rounded-lg p-4" data-testid="donate-offline-note">
              Secure online giving is being connected. To donate or partner today, email <a className="underline font-bold" href={`mailto:${EMAIL}`}>{EMAIL}</a> or use the enquiry form.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default function Support() {
  const [form, setForm] = useState({ org: "", email: "", tier: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Ngā mihi — your enquiry has been received. We've emailed you a thank-you, and a real person will be in touch within a couple of days.");
      setForm({ org: "", email: "", tier: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="support" className="bg-[#F5F2EC] text-[#1A1A1A] py-24 sm:py-32 border-t border-[#E2DCD2]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p {...rise} className="mono-label text-[#C2410C] mb-4">Community partners programme</motion.p>
        <motion.h2 {...rise} className="font-display font-extrabold uppercase text-3xl sm:text-5xl tracking-tight mb-4">Back the movement</motion.h2>
        <motion.p {...rise} className="text-[#52525B] max-w-2xl mb-12">
          Sponsorship turns this campaign into sustainable Christchurch infrastructure — education, community hui, lived-experience speakers, youth prevention and safeguarding. Sponsors never control our messages, stories or safeguarding decisions.
        </motion.p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {TIERS.map(([name, amount, benefits], i) => (
            <motion.div key={name} {...rise} transition={{ delay: i * 0.07, duration: 0.5 }}
              className={`rounded-2xl p-6 border ${i === 3 ? "bg-[#1E4B3E] text-[#F5F2EC] border-[#1E4B3E]" : "bg-white border-[#E2DCD2] hover:border-[#1E4B3E]"} transition-colors`}>
              <span className="mono-label opacity-70">{name}</span>
              <p className="font-display font-extrabold text-3xl mt-2 mb-4">{amount}</p>
              <ul className="text-xs space-y-1.5 opacity-80 leading-relaxed">
                {benefits.map((b) => <li key={b}>{b.startsWith("Everything") ? <em>{b}</em> : `— ${b}`}</li>)}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <DonateBox />
          <form onSubmit={submit} className="bg-white border border-[#E2DCD2] rounded-2xl p-7" data-testid="partner-enquiry-form">
            <h3 className="font-display font-extrabold uppercase text-xl mb-5">Enquire about partnering</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <input required value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="Organisation / your name"
                data-testid="enquiry-input-org" maxLength={120}
                className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1E4B3E]" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email"
                data-testid="enquiry-input-email"
                className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1E4B3E]" />
            </div>
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="enquiry-select-tier"
              className="w-full bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm mb-3 focus:outline-none focus:border-[#1E4B3E]">
              <option value="">I'm here about… (optional)</option>
              <option value="Join the advisory group">Join the advisory group</option>
              <option value="RSVP to the next hui">RSVP to the next hui</option>
              <option value="Volunteer with the movement">Volunteer with the movement</option>
              <option value="Media / interview request">Media / interview request</option>
              {TIERS.map(([name, amount]) => <option key={name} value={`${name} — ${amount}`}>{name} — {amount}</option>)}
            </select>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Message (optional)"
              data-testid="enquiry-input-message" maxLength={2000}
              className="w-full bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm mb-4 focus:outline-none focus:border-[#1E4B3E]" />
            <button type="submit" disabled={busy} data-testid="enquiry-submit-button"
              className="w-full bg-[#1E4B3E] hover:bg-[#16382e] disabled:opacity-50 text-white font-bold rounded-lg py-3 text-sm transition-colors">
              {busy ? "Sending…" : "Send enquiry"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
